import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useToast } from '../../context/ToastContext';
import '../commissionscan/CommissionScan.css';
import '../../styles/QuoteProposal.css';
import '../../styles/Voice.css';

const getToolboxToken = () => localStorage.getItem('toolboxToken') || '';
const getNcToken      = () => localStorage.getItem('amsToken')     || '';

const PAGE_SIZE = 5;

type SortDir          = 'asc' | 'desc' | null;
type ViolationSortKey = 'violation_code' | 'violation_desc' | 'issue_date';
type DriverSortKey    = 'first_name' | 'last_name' | 'date_of_birth' | 'license_number';

export interface MVRTransaction {
  transactionId: string;
  fileName: string;
  created: string;
  userName: string;
  status: number | string;
  driverDetailsJson?: string;
  selectionOptionsJson?: string;
  fileUrl?: string;
  isLiked?: boolean | null;
  isDriverAddedInAMS?: boolean;
  isViolationAddedInAMS?: boolean;
  driverDatabaseId?: string;
  totalCount?: number;
}

interface DriverRow {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  license_number: string;
}

export interface Violation {
  violation_code: string;
  violation_desc: string;
  issue_date: string;
}

interface ParsedMVRData {
  driver: DriverRow;
  violations: Violation[];
}

const parseData = (tx: MVRTransaction): ParsedMVRData => {
  const empty: ParsedMVRData = {
    driver: { first_name: '', last_name: '', date_of_birth: '', license_number: '' },
    violations: [],
  };
  try {
    if (!tx.driverDetailsJson) return empty;
    const d = JSON.parse(tx.driverDetailsJson);
    const rawViolations = Array.isArray(d.violation) ? d.violation
      : Array.isArray(d.violations) ? d.violations : [];
    return {
      driver: {
        first_name:     d.first_name     || d.firstName     || '',
        last_name:      d.last_name      || d.lastName      || '',
        date_of_birth:  d.date_of_birth  || d.dateOfBirth   || '',
        license_number: d.license_number || d.licenseNumber || '',
      },
      violations: rawViolations.map((v: any) => ({
        violation_code: v.driver_violation_information_code || v.violation_code || '',
        violation_desc: v.driver_violation_information_desc || v.violation_desc || '',
        issue_date:     v.issue_date || '',
      })),
    };
  } catch { return empty; }
};

const getClientInfo = (tx: MVRTransaction) => {
  try {
    if (!tx.selectionOptionsJson) return { clientName: '—', insuredClientId: '' };
    const p = JSON.parse(tx.selectionOptionsJson);
    return {
      clientName:      p.clientName      || '—',
      insuredClientId: p.insuredClientId || p.insuredId || '',
    };
  } catch { return { clientName: '—', insuredClientId: '' }; }
};

/* ── Sort icon ── */
const SortIcon: React.FC<{ dir: SortDir }> = ({ dir }) => (
  <span style={{
    marginLeft: 5, fontSize: 11, userSelect: 'none',
    color: dir ? 'var(--accent-purple)' : 'var(--text-secondary)',
  }}>
    {dir === 'asc' ? '↑' : dir === 'desc' ? '↓' : '↑↓'}
  </span>
);

/* ── Pagination ── */
const TablePagination: React.FC<{ page: number; totalPages: number; onChange: (p: number) => void }> = ({ page, totalPages, onChange }) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 12, marginBottom: 4 }}>
    <button onClick={() => onChange(page - 1)} disabled={page === 1} style={paginBtn(page === 1)}>‹</button>
    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
      <button key={p} onClick={() => onChange(p)} style={{
        width: 28, height: 28, borderRadius: 6,
        background: page === p ? 'var(--accent-purple)' : 'var(--surface-glass)',
        border: `1px solid ${page === p ? 'var(--accent-purple)' : 'var(--border-glass)'}`,
        color: page === p ? '#fff' : 'var(--text-secondary)',
        fontSize: 12, fontWeight: page === p ? 700 : 400,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'inherit',
      }}>{p}</button>
    ))}
    <button onClick={() => onChange(page + 1)} disabled={page === totalPages} style={paginBtn(page === totalPages)}>›</button>
  </div>
);

function paginBtn(disabled: boolean): React.CSSProperties {
  return {
    width: 28, height: 28, borderRadius: 6,
    background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
    color: disabled ? 'var(--text-tertiary)' : 'var(--text-secondary)',
    fontSize: 14, cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    opacity: disabled ? 0.4 : 1,
  };
}

const TH_STYLE: React.CSSProperties = {
  padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 600,
  color: 'var(--text-secondary)', background: 'var(--surface-glass)',
  cursor: 'pointer', userSelect: 'none',
  borderBottom: '1px solid var(--border-glass)', whiteSpace: 'nowrap',
};

const TD_STYLE: React.CSSProperties = {
  padding: '10px 14px', fontSize: 13,
  color: 'var(--text-primary)',
  borderBottom: '1px solid var(--border-glass)',
};

interface Props {
  tx: MVRTransaction;
  onClose: () => void;
  onAMSUpdate?: () => void;
  initialPdfUrl?: string;
  inline?: boolean;
  closeLabel?: string;
}

const MVRScanPreview: React.FC<Props> = ({
  tx, onClose, onAMSUpdate,
  initialPdfUrl, inline = false, closeLabel = '✕',
}) => {
  const { clientName, insuredClientId } = getClientInfo(tx);
  const { driver: initDriver, violations: initViolations } = useMemo(() => parseData(tx), [tx]);

  const [activeTab, setActiveTab] = useState<'driver' | 'violation'>('driver');

  const [pdfUrl,     setPdfUrl]     = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);

  /* Driver sort + pagination */
  const [drvSortKey, setDrvSortKey] = useState<DriverSortKey | null>(null);
  const [drvSortDir, setDrvSortDir] = useState<SortDir>(null);
  const [drvPage,    setDrvPage]    = useState(1);

  /* Violation sort + pagination */
  const [vioSortKey, setVioSortKey] = useState<ViolationSortKey | null>(null);
  const [vioSortDir, setVioSortDir] = useState<SortDir>(null);
  const [vioPage,    setVioPage]    = useState(1);

  const [driverId,         setDriverId]         = useState(tx.driverDatabaseId || '');
  const [driverLoading,    setDriverLoading]     = useState(false);
  const [driverError,      setDriverError]       = useState('');
  const [driverSuccess,    setDriverSuccess]     = useState(!!tx.isDriverAddedInAMS);

  const [violationLoading, setViolationLoading] = useState(false);
  const [violationError,   setViolationError]   = useState('');
  const [violationSuccess, setViolationSuccess] = useState(!!tx.isViolationAddedInAMS);

  /* Upload In AMS */
  const [amsDialogOpen,    setAmsDialogOpen]    = useState(false);
  const [amsClientQuery,   setAmsClientQuery]   = useState('');
  const [amsClientName,    setAmsClientName]    = useState('');
  const [amsClientResults, setAmsClientResults] = useState<{ name: string; databaseId: string }[]>([]);
  const [amsClientLoading, setAmsClientLoading] = useState(false);
  const [amsInsuredId,     setAmsInsuredId]     = useState('');
  const [amsUploadLoading, setAmsUploadLoading] = useState(false);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (initialPdfUrl) {
      setPdfUrl(initialPdfUrl);
      setPdfLoading(false);
      return;
    }
    setPdfLoading(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/MVRScan/previewdocument`,
      { transactionId: tx.transactionId, fileName: tx.fileName },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(res => setPdfUrl(res.data?.fileUrl ?? null))
      .catch(() => {})
      .finally(() => setPdfLoading(false));
  }, [tx.transactionId, tx.fileName, initialPdfUrl]);

  useEffect(() => {
    if (!amsClientQuery.trim()) { setAmsClientResults([]); return; }
    const t = setTimeout(() => {
      setAmsClientLoading(true);
      axios.get(`${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/customerlist`, {
        params:  { searchText: amsClientQuery, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      })
        .then(r => setAmsClientResults(r.data || []))
        .finally(() => setAmsClientLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [amsClientQuery]);

  const onUploadToAMS = async () => {
    if (!amsInsuredId) return;
    setAmsUploadLoading(true);
    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/uploadV1`,
        {
          TransactionId: tx.transactionId,
          FileName:      tx.fileName,
          NCPolicyId:    '',
          NCInsuredId:   amsInsuredId,
          FileData:      '',
          FolderName:    'MVRScan',
          TargetName:    'MVRScan',
          NowCertsToken: getNcToken(),
        },
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      if (res.data?.success) {
        showSuccess('PDF uploaded to AMS successfully.');
        setTimeout(() => setAmsDialogOpen(false), 2000);
      } else {
        showError(res.data?.errorMessage || 'Error while uploading PDF to AMS.');
      }
    } catch (err: any) {
      showError(err.response?.data?.errorMessage || 'Error while uploading PDF to AMS.');
    } finally {
      setAmsUploadLoading(false);
    }
  };

  const toggleDrvSort = (key: DriverSortKey) => {
    if (drvSortKey !== key) { setDrvSortKey(key); setDrvSortDir('asc'); }
    else if (drvSortDir === 'asc') setDrvSortDir('desc');
    else { setDrvSortKey(null); setDrvSortDir(null); }
    setDrvPage(1);
  };

  const toggleVioSort = (key: ViolationSortKey) => {
    if (vioSortKey !== key) { setVioSortKey(key); setVioSortDir('asc'); }
    else if (vioSortDir === 'asc') setVioSortDir('desc');
    else { setVioSortKey(null); setVioSortDir(null); }
    setVioPage(1);
  };

  const sortedDriverRows = useMemo(() => {
    const rows: DriverRow[] = [initDriver];
    if (!drvSortKey || !drvSortDir) return rows;
    return [...rows].sort((a, b) =>
      drvSortDir === 'asc' ? a[drvSortKey].localeCompare(b[drvSortKey]) : b[drvSortKey].localeCompare(a[drvSortKey]),
    );
  }, [initDriver, drvSortKey, drvSortDir]);
  const drvTotalPages = Math.max(1, Math.ceil(sortedDriverRows.length / PAGE_SIZE));
  const drvPagedRows  = sortedDriverRows.slice((drvPage - 1) * PAGE_SIZE, drvPage * PAGE_SIZE);

  const sortedVioRows = useMemo(() => {
    if (!vioSortKey || !vioSortDir) return initViolations;
    return [...initViolations].sort((a, b) =>
      vioSortDir === 'asc'
        ? (a[vioSortKey] || '').localeCompare(b[vioSortKey] || '')
        : (b[vioSortKey] || '').localeCompare(a[vioSortKey] || ''),
    );
  }, [initViolations, vioSortKey, vioSortDir]);
  const vioTotalPages = Math.max(1, Math.ceil(sortedVioRows.length / PAGE_SIZE));
  const vioPagedRows  = sortedVioRows.slice((vioPage - 1) * PAGE_SIZE, vioPage * PAGE_SIZE);

  const canInsertViolation = !!driverId;

  const onInsertDriver = async () => {
    if (!insuredClientId) return;
    setDriverLoading(true);
    setDriverError('');
    try {
      const requestData = [{
        insured_database_id: insuredClientId,
        first_name:          initDriver.first_name,
        last_name:           initDriver.last_name,
        date_of_birth:       initDriver.date_of_birth,
        license_number:      initDriver.license_number,
        active:              true,
      }];
      const res = await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/adddrivers?ncToken=${encodeURIComponent(getNcToken())}`,
        requestData,
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      if (res.data[0]?.status !== "1") {
        setDriverError(res.data[0]?.message || 'Error inserting driver. Please try again.');
        return;
      }
      const newDriverId = res.data[0]?.databaseId || '';
      setDriverId(newDriverId);
      setDriverSuccess(true);

      const finalData = requestData.map((item, index) => ({
        ...item,
        databaseId: res.data[index]?.databaseId,
      }));

      await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/MvrScan/update`,
        {
          TransactionId:        tx.transactionId,
          IsDriverAddedInAMS:   true,
          IsViolationAddedInAMS: false,
          SelectedDriverData:   JSON.stringify(finalData),
          SelectedViolationData: '',
        },
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      onAMSUpdate?.();
    } catch (err: any) {
      setDriverError(err.response?.data?.errorMessage || 'Error inserting driver. Please try again.');
    } finally {
      setDriverLoading(false);
    }
  };

  const onInsertViolation = async () => {
    if (!driverId) return;
    setViolationLoading(true);
    setViolationError('');
    try {
      const violationRequest = {
        driverId,
        driverViolations: initViolations.map(v => ({
          violationCode: v.violation_code,
          violationDate: v.issue_date,
          description:   v.violation_desc,
        })),
      };
      const res = await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/insertviolation?ncToken=${encodeURIComponent(getNcToken())}`,
        violationRequest,
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      if (res.data?.status !== 1) {
        setViolationError(res.data?.message || 'Error inserting violations. Please try again.');
        return;
      }
      setViolationSuccess(true);

      await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/MvrScan/update`,
        {
          TransactionId:         tx.transactionId,
          IsDriverAddedInAMS:    true,
          IsViolationAddedInAMS: true,
          SelectedDriverData:    '',
          SelectedViolationData: JSON.stringify(violationRequest),
        },
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      onAMSUpdate?.();
    } catch (err: any) {
      setViolationError(err.response?.data?.errorMessage || 'Error inserting violations. Please try again.');
    } finally {
      setViolationLoading(false);
    }
  };

  const panel = (
    <div style={{
      width:      inline ? '100%' : '92vw',
      maxWidth:   inline ? undefined : 1260,
      height:     inline ? '100%' : '90vh',
      display:    'flex', flexDirection: 'column',
      borderRadius: inline ? 0 : 12,
      background: 'var(--bg-primary)',
      boxShadow:  inline ? 'none' : '0 20px 60px rgba(0,0,0,0.3)',
      overflow:   'hidden',
      border:     inline ? 'none' : '1px solid var(--border-glass)',
    }}>

      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, padding: '10px 20px', flexShrink: 0,
        borderBottom: '1px solid var(--border-glass)',
        background: 'var(--surface-glass)',
      }}>
        {closeLabel !== '✕' ? (
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600,
            padding: '4px 6px', borderRadius: 6, fontFamily: 'inherit',
          }}>← {closeLabel}</button>
        ) : (
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
            Client: <strong style={{ color: 'var(--accent-purple)' }}>{clientName}</strong>
          </span>
        )}
        {closeLabel === '✕' && (
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-secondary)', fontSize: 18, lineHeight: 1, padding: '4px 6px', borderRadius: 6,
          }}>✕</button>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', minHeight: 0 }}>

        {/* Left: PDF viewer */}
        <div className="cspr-pdf-panel">
          <div className="cspr-pdf-panel__header">
            <span className="cspr-pdf-panel__title">Uploaded Document</span>
            {pdfUrl && !inline && (
              <button
                className="cspr-action-btn cspr-action-btn--purple"
                onClick={() => {
                  setAmsClientQuery('');
                  setAmsClientName('');
                  setAmsClientResults([]);
                  setAmsInsuredId('');
                  setAmsDialogOpen(true);
                }}
                style={{ fontSize: 12, padding: '5px 12px' }}
              >
                Upload In AMS
              </button>
            )}
          </div>
          <div className="cspr-pdf-viewer">
            {pdfUrl
              ? <iframe src={`${pdfUrl}#zoom=90`} title="MVR Preview" style={{ width: '100%', height: '100%', border: 'none' }} />
              : pdfLoading
                ? <div className="cspr-pdf-placeholder">Loading document…</div>
                : <div className="cspr-pdf-placeholder">Preview not available</div>
            }
          </div>
        </div>

        {/* Right: tab panel */}
        <div style={{
          flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
          background: 'var(--bg-primary)', overflow: 'hidden',
        }}>

          {/* Tab bar */}
          <div style={{
            display: 'flex', flexShrink: 0,
            borderBottom: '1px solid var(--border-glass)',
            background: 'var(--bg-primary)',
          }}>
            {(['driver', 'violation'] as const).map(t => (
              <button key={t} onClick={() => setActiveTab(t)} style={{
                padding: '10px 20px', fontSize: 13,
                fontWeight: activeTab === t ? 700 : 500,
                color: activeTab === t ? 'var(--accent-purple)' : 'var(--text-secondary)',
                background: 'none', border: 'none',
                borderBottom: activeTab === t ? '2px solid var(--accent-purple)' : '2px solid transparent',
                cursor: 'pointer', transition: 'all 0.15s',
                fontFamily: 'inherit', marginBottom: -1,
              }}>
                {t === 'driver' ? 'Driver Details' : 'Violation Details'}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>

            {/* Driver Details Tab */}
            {activeTab === 'driver' && (
              <div style={{
                minHeight: '100%', display: 'flex', flexDirection: 'column',
                background: 'var(--surface-glass)',
                border: '1px solid var(--border-glass)',
                borderRadius: 10, overflow: 'hidden',
              }}>
                <div style={{ padding: '13px 20px', borderBottom: '1px solid var(--border-glass)', flexShrink: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Driver Information</span>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px 20px' }}>
                  {driverError && <p style={{ color: '#ef4444', fontSize: 12, marginBottom: 14 }}>{driverError}</p>}

                  {!(initDriver.first_name || initDriver.last_name || initDriver.date_of_birth || initDriver.license_number) ? (
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: '20px 0' }}>
                      No Driver Details found.
                    </p>
                  ) : (
                    <>
                      <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-glass)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              {([
                                ['first_name',     'First Name'],
                                ['last_name',      'Last Name'],
                                ['date_of_birth',  'Date Of Birth'],
                                ['license_number', 'License Number'],
                              ] as [DriverSortKey, string][]).map(([key, label]) => (
                                <th key={key} style={TH_STYLE} onClick={() => toggleDrvSort(key)}>
                                  {label}<SortIcon dir={drvSortKey === key ? drvSortDir : null} />
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {drvPagedRows.map((row, i) => (
                              <tr key={i} style={{ background: i % 2 === 1 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                                <td style={TD_STYLE}>{row.first_name     || '—'}</td>
                                <td style={TD_STYLE}>{row.last_name      || '—'}</td>
                                <td style={TD_STYLE}>{row.date_of_birth  || '—'}</td>
                                <td style={TD_STYLE}>{row.license_number || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <TablePagination page={drvPage} totalPages={drvTotalPages} onChange={setDrvPage} />
                    </>
                  )}

                  {(initDriver.first_name || initDriver.last_name || initDriver.date_of_birth || initDriver.license_number) && (
                  <div style={{
                    marginTop: 'auto', paddingTop: 16,
                    borderTop: '1px solid var(--border-glass)',
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12,
                  }}>
                    {driverSuccess ? (
                      <span style={{ color: '#10b981', fontSize: 13, fontWeight: 600 }}>✓ Driver inserted into AMS!</span>
                    ) : (
                      <button
                        className="cspr-action-btn cspr-action-btn--purple"
                        disabled={driverLoading || !insuredClientId}
                        onClick={onInsertDriver}
                        style={{ opacity: (driverLoading || !insuredClientId) ? 0.5 : 1, cursor: (driverLoading || !insuredClientId) ? 'not-allowed' : 'pointer' }}
                      >
                        {driverLoading ? 'Inserting…' : 'Insert Driver'}
                      </button>
                    )}
                  </div>
                  )}
                </div>
              </div>
            )}

            {/* Violation Details Tab */}
            {activeTab === 'violation' && (
              <div style={{
                minHeight: '100%', display: 'flex', flexDirection: 'column',
                background: 'var(--surface-glass)',
                border: '1px solid var(--border-glass)',
                borderRadius: 10, overflow: 'hidden',
              }}>
                <div style={{ padding: '13px 20px', borderBottom: '1px solid var(--border-glass)', flexShrink: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Violation Details</span>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px 20px' }}>
                  {violationError && <p style={{ color: '#ef4444', fontSize: 12, marginBottom: 12 }}>{violationError}</p>}

                  {initViolations.length === 0 ? (
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: '20px 0' }}>
                      No violations found.
                    </p>
                  ) : (
                    <>
                      <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-glass)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              {([
                                ['violation_code', 'Violation Code'],
                                ['violation_desc', 'Violation Description'],
                                ['issue_date',     'Issue Date'],
                              ] as [ViolationSortKey, string][]).map(([key, label]) => (
                                <th key={key} style={TH_STYLE} onClick={() => toggleVioSort(key)}>
                                  {label}<SortIcon dir={vioSortKey === key ? vioSortDir : null} />
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {vioPagedRows.map((v, i) => (
                              <tr key={i} style={{ background: i % 2 === 1 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                                <td style={TD_STYLE}>{v.violation_code || '—'}</td>
                                <td style={TD_STYLE}>{v.violation_desc || '—'}</td>
                                <td style={TD_STYLE}>{v.issue_date     || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <TablePagination page={vioPage} totalPages={vioTotalPages} onChange={setVioPage} />
                    </>
                  )}

                  <div style={{
                    marginTop: 'auto', paddingTop: 16,
                    borderTop: '1px solid var(--border-glass)',
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12,
                  }}>
                    {violationSuccess ? (
                      <span style={{ color: '#10b981', fontSize: 13, fontWeight: 600 }}>✓ Violations inserted into AMS!</span>
                    ) : canInsertViolation ? (
                      <button
                        className="cspr-action-btn cspr-action-btn--purple"
                        disabled={violationLoading || initViolations.length === 0}
                        onClick={onInsertViolation}
                        style={{ opacity: (violationLoading || initViolations.length === 0) ? 0.5 : 1, cursor: (violationLoading || initViolations.length === 0) ? 'not-allowed' : 'pointer' }}
                      >
                        {violationLoading ? 'Inserting…' : 'Insert Violation'}
                      </button>
                    ) : initViolations.length > 0 ? (
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                        Insert driver first to enable violation insert.
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );

  const amsDialogPortal = amsDialogOpen ? ReactDOM.createPortal(
    <div
      className="csams-overlay"
      onClick={() => setAmsDialogOpen(false)}
      style={{ zIndex: 1200, background: 'rgba(0,0,0,0.55)' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '90vw', maxWidth: 420,
          background: 'var(--bg-primary)', borderRadius: 12,
          border: '1px solid var(--border-glass)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          padding: '24px',
        }}
      >
        <h4 style={{ margin: '0 0 18px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
          Upload In AMS
        </h4>

        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
          Search Client
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            autoFocus
            placeholder="Enter client name…"
            value={amsClientQuery}
            onChange={e => {
              setAmsClientQuery(e.target.value);
              setAmsClientName('');
              setAmsInsuredId('');
              if (!e.target.value) setAmsClientResults([]);
            }}
            style={{
              width: '100%', padding: '8px 12px', boxSizing: 'border-box',
              background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
              borderRadius: 7, fontSize: 13, color: 'var(--text-primary)', outline: 'none',
            }}
          />
          {amsClientLoading && (
            <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--text-secondary)' }}>
              …
            </span>
          )}
          {amsClientResults.length > 0 && (
            <ul style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
              background: 'var(--bg-primary)', border: '1px solid var(--border-glass)',
              borderRadius: 7, margin: '4px 0 0', padding: 0, listStyle: 'none',
              maxHeight: 180, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            }}>
              {amsClientResults.map(c => (
                <li
                  key={c.databaseId}
                  onClick={() => {
                    setAmsClientName(c.name);
                    setAmsInsuredId(c.databaseId);
                    setAmsClientQuery(c.name);
                    setAmsClientResults([]);
                  }}
                  style={{
                    padding: '9px 14px', fontSize: 13, cursor: 'pointer',
                    color: 'var(--text-primary)', borderBottom: '1px solid var(--border-glass)',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(124,58,237,0.08)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  {c.name}
                </li>
              ))}
            </ul>
          )}
          {amsClientQuery.trim() && !amsClientLoading && amsClientResults.length === 0 && !amsInsuredId && (
            <ul style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
              background: 'var(--bg-primary)', border: '1px solid var(--border-glass)',
              borderRadius: 7, margin: '4px 0 0', padding: 0, listStyle: 'none',
            }}>
              <li style={{ padding: '9px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>No clients found</li>
            </ul>
          )}
        </div>

        {amsClientName && amsInsuredId && (
          <p style={{ margin: '10px 0 0', fontSize: 12, color: '#10b981', fontWeight: 600 }}>
            ✓ {amsClientName}
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button
            onClick={() => setAmsDialogOpen(false)}
            style={{
              padding: '8px 18px', borderRadius: 7, fontSize: 13, fontWeight: 600,
              background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
              color: 'var(--text-secondary)', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            className="cspr-action-btn cspr-action-btn--purple"
            disabled={!amsInsuredId || amsUploadLoading}
            onClick={onUploadToAMS}
            style={{ opacity: (!amsInsuredId || amsUploadLoading) ? 0.5 : 1, cursor: (!amsInsuredId || amsUploadLoading) ? 'not-allowed' : 'pointer' }}
          >
            {amsUploadLoading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  ) : null;

  if (inline) return <>{panel}{amsDialogPortal}</>;

  return <>
    {ReactDOM.createPortal(
      <div
        className="csams-overlay"
        onClick={onClose}
        style={{ zIndex: 1100, background: 'rgba(0,0,0,0.55)' }}
      >
        <div onClick={e => e.stopPropagation()}>{panel}</div>
      </div>,
      document.body,
    )}
    {amsDialogPortal}
  </>;
};

export default MVRScanPreview;

